export type ReportPreviewKind = 'html' | 'pdf' | 'text' | 'docx' | 'markdown' | 'unsupported';

export interface ReportPreviewContent {
    kind: ReportPreviewKind;
    html?: string;
    text?: string;
    blobUrl?: string;
    contentType?: string;
    fileLabel?: string;
}

const DOCX_ZIP_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];
const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46];

const matchesSignature = (bytes: Uint8Array, signature: number[]): boolean =>
    signature.every((value, index) => bytes[index] === value);

const extensionFromUrl = (url: string): string => {
    try {
        const pathname = new URL(url).pathname;
        return (pathname.split('.').pop() ?? '').toLowerCase();
    } catch {
        return '';
    }
};

const isMostlyText = (bytes: Uint8Array): boolean => {
    const len = Math.min(bytes.length, 2048);
    if (!len) return false;

    let nonPrintable = 0;
    for (let i = 0; i < len; i += 1) {
        const byte = bytes[i];
        if (byte === 9 || byte === 10 || byte === 13) continue;
        if (byte < 32 || byte === 127) nonPrintable += 1;
    }
    return nonPrintable / len < 0.05;
};

const detectReportPreviewKind = (
    bytes: Uint8Array,
    contentType: string,
    url: string
): ReportPreviewKind => {
    if (matchesSignature(bytes, PDF_SIGNATURE)) return 'pdf';
    if (matchesSignature(bytes, DOCX_ZIP_SIGNATURE)) return 'docx';

    const normalizedType = contentType.toLowerCase();
    if (normalizedType.includes('pdf')) return 'pdf';
    if (normalizedType.includes('wordprocessingml') || normalizedType.includes('msword')) return 'docx';
    if (normalizedType.includes('html')) return 'html';
    if (normalizedType.includes('text/plain')) return 'text';

    const ext = extensionFromUrl(url);
    if (ext === 'pdf') return 'pdf';
    if (ext === 'docx' || ext === 'doc') return 'docx';
    if (ext === 'txt') return 'text';
    if (ext === 'html' || ext === 'htm') return 'html';
    if (ext === 'md' || ext === 'markdown') return 'markdown';

    const sample = new TextDecoder('utf-8', { fatal: false }).decode(bytes.slice(0, 512)).trim();
    if (sample.startsWith('<') || sample.toLowerCase().includes('<!doctype html')) return 'html';
    if (isMostlyText(bytes)) return 'text';

    return 'unsupported';
};

export const loadReportPreviewFromUrl = async (url: string): Promise<ReportPreviewContent> => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Failed to fetch report file');
    }

    const contentType = response.headers.get('content-type') ?? '';
    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const kind = detectReportPreviewKind(bytes, contentType, url);

    switch (kind) {
        case 'pdf': {
            const blob = new Blob([buffer], { type: 'application/pdf' });
            return {
                kind,
                blobUrl: URL.createObjectURL(blob),
                contentType,
            };
        }
        case 'docx': {
            const mammoth = await import('mammoth');
            const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
            return {
                kind,
                html: result.value,
                contentType,
                fileLabel: 'Word document',
            };
        }
        case 'html': {
            const html = new TextDecoder('utf-8').decode(buffer);
            return { kind, html, contentType };
        }
        case 'markdown': {
            const raw = new TextDecoder('utf-8').decode(buffer);
            const html = markdownToHtml(raw);
            return { kind: 'html', html, contentType };
        }
        case 'text': {
            const text = new TextDecoder('utf-8').decode(buffer);
            return { kind, text, contentType };
        }
        default:
            return {
                kind: 'unsupported',
                contentType,
                fileLabel: extensionFromUrl(url) || 'file',
            };
    }
};

export const revokeReportPreviewBlob = (preview: ReportPreviewContent | null): void => {
    if (preview?.blobUrl) {
        URL.revokeObjectURL(preview.blobUrl);
    }
};

// Lightweight markdown → HTML converter (no external dependency).
const markdownToHtml = (md: string): string => {
    const escaped = md
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const lines = escaped.split('\n');
    const out: string[] = [];
    let inCode = false;
    let inList = false;

    for (const raw of lines) {
        const line = raw.trimEnd();

        if (line.startsWith('```')) {
            if (!inCode) { out.push('<pre><code>'); inCode = true; }
            else { out.push('</code></pre>'); inCode = false; }
            continue;
        }
        if (inCode) { out.push(line); continue; }

        // Close open list before non-list line
        const isBullet = /^([-*+]|\d+\.)\s/.test(line);
        if (inList && !isBullet) { out.push('</ul>'); inList = false; }

        if (!line.trim()) { out.push('<br>'); continue; }

        if (line.startsWith('###### ')) { out.push(`<h6>${inline(line.slice(7))}</h6>`); continue; }
        if (line.startsWith('##### '))  { out.push(`<h5>${inline(line.slice(6))}</h5>`); continue; }
        if (line.startsWith('#### '))   { out.push(`<h4>${inline(line.slice(5))}</h4>`); continue; }
        if (line.startsWith('### '))    { out.push(`<h3>${inline(line.slice(4))}</h3>`); continue; }
        if (line.startsWith('## '))     { out.push(`<h2>${inline(line.slice(3))}</h2>`); continue; }
        if (line.startsWith('# '))      { out.push(`<h1>${inline(line.slice(2))}</h1>`); continue; }
        if (/^---+$/.test(line))        { out.push('<hr>'); continue; }

        if (isBullet) {
            if (!inList) { out.push('<ul>'); inList = true; }
            out.push(`<li>${inline(line.replace(/^([-*+]|\d+\.)\s/, ''))}</li>`);
            continue;
        }

        out.push(`<p>${inline(line)}</p>`);
    }
    if (inList) out.push('</ul>');
    if (inCode) out.push('</code></pre>');

    return `<html><body style="font-family:sans-serif;line-height:1.6;padding:1.5rem;max-width:820px;margin:0 auto">${out.join('\n')}</body></html>`;
};

const inline = (s: string): string =>
    s
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>')
        .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
